import { FormEvent, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { createWatchlist, errorMessage, searchLive, searchLocal, searchLocalPages, LocalSearchParams, LocalSort, SearchOutcome } from './api';
import { categoryLabel } from './categories';
import AdItem from './AdItem';
import ArchiveFilters from './ArchiveFilters';
import CompanyResults, { MAX_MONITORED_PAGES } from './CompanyResults';
import PageBack from './PageBack';

export const COUNTRIES = [
  { code: 'IT', label: 'Italia' },
  { code: 'DE', label: 'Germania' },
  { code: 'FR', label: 'Francia' },
  { code: 'ES', label: 'Spagna' },
  { code: 'GB', label: 'Regno Unito' },
  { code: 'NL', label: 'Paesi Bassi' },
  { code: 'BE', label: 'Belgio' },
  { code: 'AT', label: 'Austria' },
  { code: 'PT', label: 'Portogallo' },
  { code: 'PL', label: 'Polonia' },
  { code: 'SE', label: 'Svezia' },
  { code: 'DK', label: 'Danimarca' },
  { code: 'FI', label: 'Finlandia' },
  { code: 'IE', label: 'Irlanda' },
  { code: 'GR', label: 'Grecia' },
  { code: 'CZ', label: 'Cechia' },
  { code: 'RO', label: 'Romania' },
  { code: 'HU', label: 'Ungheria' },
  { code: 'HR', label: 'Croazia' },
  { code: 'CH', label: 'Svizzera' },
];

const PAGE_SIZE = 20;

const PLATFORM_LABEL: Record<string, string> = {
  facebook: 'Facebook',
  instagram: 'Instagram',
  audience_network: 'Audience Network',
  messenger: 'Messenger',
};

const SORT_LABEL: Record<string, string> = {
  reach: 'per reach EU',
  days: 'per durata',
  startTime: 'per data di partenza',
};

export default function Feed() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const q = params.get('q') ?? '';
  const pageId = params.get('pageId') ?? '';
  const pageName = params.get('pageName') ?? '';
  const category = params.get('category') ?? '';
  const platform = params.get('platform') ?? '';
  const activeParam = params.get('active'); // 'true' | 'false' | null
  const startFrom = params.get('from') ?? '';
  const startTo = params.get('to') ?? '';
  const location = params.get('location') ?? '';
  const countryFilter = params.get('country') ?? '';
  const language = params.get('language') ?? '';
  const gender = params.get('gender') ?? '';
  const age = params.get('age') ?? '';
  const minReach = params.get('minReach') ?? '';
  const minDays = params.get('minDays') ?? '';
  const minVariants = params.get('minVariants') ?? '';
  const newDays = params.get('newDays') ?? '';
  const sort = (params.get('sort') as LocalSort | null) ?? 'recent';

  const [country, setCountry] = useState('IT');
  const [mobileQuery, setMobileQuery] = useState(q);
  const [liveOutcome, setLiveOutcome] = useState<SearchOutcome | null>(null);
  const [selectedPageIds, setSelectedPageIds] = useState<string[]>([]);

  const queryClient = useQueryClient();

  useEffect(() => setMobileQuery(q), [q]);

  function submitMobileSearch(event: FormEvent) {
    event.preventDefault();
    const next = new URLSearchParams(params);
    const value = mobileQuery.trim();
    if (value) next.set('q', value); else next.delete('q');
    setParams(next);
  }

  const groupedMode = Boolean(q && !pageId);
  const sharedFilters: LocalSearchParams = {
    q, category, platform,
    active: activeParam === null ? undefined : activeParam === 'true',
    startFrom, startTo, location, country: countryFilter, language,
    gender: (gender || undefined) as 'Women' | 'Men' | 'All' | undefined,
    age: age ? Number(age) : undefined,
    minReach: minReach ? Number(minReach) : undefined,
    minDays: minDays ? Number(minDays) : undefined,
    minVariants: minVariants ? Number(minVariants) : undefined,
    newDays: newDays ? Number(newDays) : undefined,
    sort,
  };

  const local = useInfiniteQuery({
    queryKey: [
      'local', q, pageId, category, platform, activeParam, startFrom, startTo,
      location, countryFilter, language, gender, age, minReach, minDays, minVariants,
      newDays, sort,
    ],
    queryFn: ({ pageParam }) =>
      searchLocal({
        ...sharedFilters,
        pageId,
        page: pageParam,
        size: PAGE_SIZE,
      }),
    initialPageParam: 0,
    getNextPageParam: (last) => (last.last ? undefined : last.number + 1),
    placeholderData: keepPreviousData,
    enabled: !groupedMode,
  });

  const pageGroups = useQuery({
    queryKey: [
      'local-page-groups', q, category, platform, activeParam, startFrom, startTo,
      location, countryFilter, language, gender, age, minReach, minDays, minVariants,
      newDays,
    ],
    queryFn: () => searchLocalPages(sharedFilters),
    placeholderData: keepPreviousData,
    enabled: groupedMode,
  });

  const live = useMutation({
    mutationFn: (p: { q: string; country?: string }) =>
      searchLive({ searchTerms: p.q, countries: [p.country ?? country], origin: 'feed' }),
    onSuccess: (outcome) => {
      setLiveOutcome(outcome);
      queryClient.invalidateQueries({ queryKey: ['local'] });
      queryClient.invalidateQueries({ queryKey: ['local-page-groups'] });
    },
  });

  const saveSelection = useMutation({
    mutationFn: () => createWatchlist({
      name: `${q.trim()} · ${selectedPageIds.length} ${selectedPageIds.length === 1 ? 'pagina' : 'pagine'}`,
      searchTerms: q.trim(),
      countries: [countryFilter || country],
      category: category || undefined,
      limit: 100,
      pageIds: selectedPageIds,
    }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['watchlists'] }),
  });

  useEffect(() => {
    setSelectedPageIds([]);
    saveSelection.reset();
    // Il cambio keyword apre un nuovo contesto di selezione.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, category, platform, activeParam, startFrom, startTo, location, countryFilter,
    language, gender, age, minReach, minDays, minVariants, newDays]);

  // Arrivo dalla topbar in modalità "Su Meta": ?live=1&country=XX lancia
  // subito l'ingest, poi i param vengono consumati (replace, niente history)
  useEffect(() => {
    if (params.get('live') !== '1' || !q) return;
    const liveCountry = params.get('country') ?? 'IT';
    setCountry(liveCountry);
    const next = new URLSearchParams(params);
    next.delete('live');
    next.delete('country');
    setParams(next, { replace: true });
    live.mutate({ q, country: liveCountry });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.get('live'), q]);

  function dropParam(...names: string[]) {
    const next = new URLSearchParams(params);
    names.forEach((n) => next.delete(n));
    setParams(next);
  }

  function filterByPage(id: string, name: string) {
    if (id === pageId) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    const next = new URLSearchParams();
    next.set('pageId', id);
    next.set('pageName', name);
    navigate(`/annunci?${next.toString()}`, {
      state: {
        from: `${routerLocation.pathname}${routerLocation.search}`,
        backLabel: q ? `Risultati per «${q}»` : 'Annunci',
      },
    });
    window.scrollTo({ top: 0 });
  }

  const ads = local.data?.pages.flatMap((p) => p.content) ?? [];
  const groups = pageGroups.data ?? [];
  const total = groupedMode
    ? groups.reduce((sum, group) => sum + group.adsCount, 0)
    : local.data?.pages[0]?.totalElements ?? 0;
  const hasFilters = Boolean(
    q || pageId || category || platform || activeParam || startFrom || startTo ||
    location || countryFilter || language || gender || age || minReach || minDays ||
    minVariants || newDays,
  );
  const searchSuccess = groupedMode ? pageGroups.isSuccess : local.isSuccess;
  const searchPending = groupedMode ? pageGroups.isPending : local.isPending;
  const searchError = groupedMode ? pageGroups.error : local.error;
  const archiveEmpty = !hasFilters && searchSuccess && total === 0;
  const noResults = hasFilters && searchSuccess && total === 0;

  function toggleSelected(page: string) {
    saveSelection.reset();
    setSelectedPageIds((current) => current.includes(page) ? current.filter((id) => id !== page) : current.length < MAX_MONITORED_PAGES ? [...current, page] : current);
  }

  function toggleAllGroups(pageIds: string[]) {
    saveSelection.reset();
    setSelectedPageIds(pageIds.length > 0 && pageIds.every((id) => selectedPageIds.includes(id)) ? [] : pageIds);
  }

  // Scorciatoie rapide (toggle): se attive, il filtro NON è duplicato tra le chip
  const quick = [
    { label: 'Attivi', name: 'active', value: 'true' },
    { label: '30gg+ in onda', name: 'minDays', value: '30' },
    { label: 'Reach alto', name: 'minReach', value: '1000' },
    { label: 'Nuovi', name: 'newDays', value: '7' },
    { label: 'In A/B test', name: 'minVariants', value: '2' },
  ];
  const isQuickOn = (name: string, value: string) =>
    quick.some((f) => f.name === name && f.value === value) && params.get(name) === value;

  // Chip riassuntive dei filtri avanzati attivi (rimuovibili singolarmente).
  // La ricerca attiva è la PRIMA chip: arrivando da un link (hashtag, trend)
  // deve essere evidente su cosa si sta filtrando.
  const chips: { label: string; remove: () => void }[] = [];
  if (q) chips.push({ label: `Ricerca: «${q}»`, remove: () => dropParam('q') });
  if (pageId)
    chips.push({ label: `Pagina: ${pageName || pageId}`, remove: () => dropParam('pageId', 'pageName') });
  if (category)
    chips.push({ label: categoryLabel(category)!, remove: () => dropParam('category') });
  if (platform)
    chips.push({ label: PLATFORM_LABEL[platform] ?? platform, remove: () => dropParam('platform') });
  if (activeParam && !isQuickOn('active', activeParam))
    chips.push({
      label: activeParam === 'true' ? 'Solo attivi' : 'Solo conclusi',
      remove: () => dropParam('active'),
    });
  if (startFrom) chips.push({ label: `Dal ${startFrom}`, remove: () => dropParam('from') });
  if (startTo) chips.push({ label: `Fino al ${startTo}`, remove: () => dropParam('to') });
  if (location) chips.push({ label: `Zona: ${location}`, remove: () => dropParam('location') });
  if (countryFilter)
    chips.push({ label: `Paese: ${countryFilter}`, remove: () => dropParam('country') });
  if (language)
    chips.push({ label: `Lingua: ${language.toUpperCase()}`, remove: () => dropParam('language') });
  if (gender)
    chips.push({
      label: gender === 'Women' ? 'Target donne' : gender === 'Men' ? 'Target uomini' : 'Target tutti',
      remove: () => dropParam('gender'),
    });
  if (age) chips.push({ label: `Target ${age} anni`, remove: () => dropParam('age') });
  if (minReach && !isQuickOn('minReach', minReach))
    chips.push({ label: `Reach ≥ ${minReach}`, remove: () => dropParam('minReach') });
  if (minDays && !isQuickOn('minDays', minDays))
    chips.push({ label: `In onda ≥ ${minDays}gg`, remove: () => dropParam('minDays') });
  if (minVariants && !isQuickOn('minVariants', minVariants))
    chips.push({ label: 'In A/B test', remove: () => dropParam('minVariants') });
  if (newDays && !isQuickOn('newDays', newDays))
    chips.push({ label: `Nuovi (${newDays}gg)`, remove: () => dropParam('newDays') });
  if (sort !== 'recent')
    chips.push({ label: `Ordine: ${SORT_LABEL[sort]}`, remove: () => dropParam('sort') });

  // Toggle dei filtri rapidi
  function toggleParam(name: string, value: string) {
    const next = new URLSearchParams(params);
    if (next.get(name) === value) next.delete(name);
    else next.set(name, value);
    setParams(next);
  }

  const quickButtons = (mobile = false) => quick.filter((item) => !mobile || ['active', 'newDays', 'minDays'].includes(item.name)).sort((a, b) => mobile ? ['active', 'newDays', 'minDays'].indexOf(a.name) - ['active', 'newDays', 'minDays'].indexOf(b.name) : 0).map((f) => {
    const on = params.get(f.name) === f.value;
    return <button key={f.label} type="button" aria-pressed={on} className={on ? 'quick quick-on' : 'quick'} onClick={() => toggleParam(f.name, f.value)}>{mobile && f.name === 'minDays' ? '30gg+' : f.label}</button>;
  });
  const navigationState = routerLocation.state as { from?: string; backLabel?: string } | null;
  const reportUrl = `/report?ids=${encodeURIComponent(selectedPageIds.join(','))}`;

  return (
    <div className="ads-page">
      <div className="page-intro ads-heading"><div><p className="eyebrow">Esplora</p><div className="title-line"><h1>Annunci</h1></div>{groupedMode && pageGroups.isSuccess && <p className="ads-page-count">{groups.length} {groups.length === 1 ? 'pagina trovata' : 'pagine trovate'}</p>}</div><Link className="import-link" to="/ricerca">Cerca su Meta</Link></div>
      {pageId && <section className="page-context"><PageBack fallback="/annunci" label="Tutti gli annunci" /><div><p className="eyebrow">Pagina selezionata</p><h2>{pageName || pageId}</h2><p>Tutte le creatività presenti in archivio, senza il filtro della ricerca precedente.</p></div></section>}
      {!pageId && q && navigationState?.from && <div className="search-context-back"><PageBack fallback="/annunci" label="Torna alla pagina precedente" /></div>}
      <form className="mobile-archive-search" role="search" onSubmit={submitMobileSearch}>
        <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="11" cy="11" r="6.5"/><path d="m16 16 4 4"/></svg>
        <input type="search" value={mobileQuery} onChange={(event) => setMobileQuery(event.target.value)} placeholder="Cerca annunci o competitor" aria-label="Cerca annunci o competitor" />
      </form>
      <div className="ads-mobile-controls">
        <div className="quick-row" role="group" aria-label="Filtri rapidi">{quickButtons(true)}</div>
        <ArchiveFilters />
      </div>
      <div className="ads-layout">
      <aside className="ads-filters"><ArchiveFilters alwaysOpen /></aside>
      <div className="ads-results">
      {chips.length > 0 && (
        <p className="filter-row">
          {chips.map((c) => (
            <button key={c.label} type="button" className="chip-clear" onClick={c.remove}>
              {c.label} ✕
            </button>
          ))}
        </p>
      )}

      {/* Azione live: esplicita e separata dalla ricerca in cache */}
      {q && !pageId && (
        <details className="live-row">
          <summary><span><strong>Aggiorna da Meta</strong><small>L’archivio resta istantaneo; la ricerca live usa quota.</small></span><span>Cerca nuovi</span></summary>
          <div className="live-body" aria-label="Ricerca live su Meta">
          <div className="live-controls">
            <select
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              aria-label="Paese per la ricerca su Meta"
            >
              {COUNTRIES.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.label}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn-live"
              disabled={live.isPending}
              onClick={() => live.mutate({ q })}
            >
              {live.isPending ? 'Interrogo Meta…' : 'Cerca su Meta'}
            </button>
          </div>
          <p className="live-more">
            Ti servono più filtri (paese, periodo, formato)? Usa la{' '}
            <Link to="/ricerca">ricerca avanzata</Link>.
          </p>
          {liveOutcome && (
            <p className="live-outcome" role="status">
              {liveOutcome.inserted > 0
                ? `${liveOutcome.inserted} annunci nuovi in archivio`
                : 'Nessun annuncio nuovo'}
              {liveOutcome.updated > 0 && `, ${liveOutcome.updated} aggiornati`}
              {liveOutcome.total === 0 && ': Meta non ha risultati per questa ricerca'}
            </p>
          )}
          {live.isError && <p className="error">{errorMessage(live.error)}</p>}
          </div>
        </details>
      )}

      {searchError && (
        <div className="notice notice-error">
          <p>{errorMessage(searchError)}</p>
          <button type="button" onClick={() => groupedMode ? pageGroups.refetch() : local.refetch()}>
            Riprova
          </button>
        </div>
      )}

      {searchPending && (groupedMode ? <div className="company-loading" role="status"><span className="sr-only">Caricamento aziende</span>{[0, 1, 2, 3].map((item) => <div key={item} aria-hidden="true"><span/><span/><span/></div>)}</div> : <div className="ads-loading" aria-label="Caricamento annunci">{[0, 1, 2, 3].map((i) => <div key={i} className="ad skeleton"><div className="sk-line sk-w40" /><div className="sk-line sk-w25" /><div className="sk-line" /><div className="sk-line" /><div className="sk-line sk-w60" /></div>)}</div>)}

      {archiveEmpty && (
        <section className="empty">
          <h2>L'archivio è vuoto</h2>
          <p>
            Osservio conserva gli annunci Facebook e Instagram dei competitor man mano che li
            cerchi. Parti da una keyword del tuo settore dalla barra qui sopra, oppure usa la{' '}
            <Link to="/ricerca">ricerca avanzata</Link> per la prima ingestion da Meta.
          </p>
        </section>
      )}

      {noResults && (
        <section className="empty">
          <h2>{groupedMode ? 'Nessuna azienda trovata' : 'Niente in archivio'}</h2>
          <p>
            Nessun annuncio salvato con questi filtri.
            {q && !pageId && ' Usa «Cerca su Meta» qui sopra per interrogare l’Ad Library.'}
          </p>
        </section>
      )}

      {groupedMode && groups.length > 0 && <>
        <p className="results-meta" role="status"><span><strong>{groups.length}</strong> {groups.length === 1 ? 'azienda' : 'aziende'} · {total} {total === 1 ? 'annuncio corrispondente' : 'annunci corrispondenti'} per «{q}»</span></p>
        <CompanyResults rows={groups} selected={selectedPageIds} sort={sort} onToggle={toggleSelected} onToggleAll={toggleAllGroups} onOpen={filterByPage} />
      </>}

      {!groupedMode && ads.length > 0 && (
        <>
          <p className="results-meta" role="status">
            {total} {total === 1 ? 'annuncio' : 'annunci'}
            {q && <> per «{q}»</>}
            {sort !== 'recent' && <> · {SORT_LABEL[sort]}</>}
            {hasFilters && (
              <button type="button" className="link-btn" onClick={() => setParams({})}>
                Mostra tutto l'archivio
              </button>
            )}
          </p>
          <div className="feed ads-feed">
            {ads.map((ad) => (
              <AdItem key={ad.id} ad={ad} onFilterPage={filterByPage} />
            ))}
          </div>
          {local.hasNextPage && (
            <button
              type="button"
              className="btn-more"
              disabled={local.isFetchingNextPage}
              onClick={() => local.fetchNextPage()}
            >
              {local.isFetchingNextPage ? 'Carico…' : 'Carica altri'}
            </button>
          )}
        </>
      )}
      </div>
      </div>
      {groupedMode && selectedPageIds.length > 0 && <aside className="monitor-selection-bar" aria-label="Azioni sulle pagine selezionate"><div><strong>{selectedPageIds.length} {selectedPageIds.length === 1 ? 'pagina selezionata' : 'pagine selezionate'}</strong><span>Segui i nuovi annunci o genera un confronto.</span></div><div className="selection-actions"><Link className="btn-secondary" to={reportUrl} state={{ from: `${routerLocation.pathname}${routerLocation.search}`, backLabel: 'Risultati annunci' }}>Report</Link>{saveSelection.isSuccess ? <Link className="btn-primary" to="/monitoraggi?tab=monitoraggi">Apri attività</Link> : <button type="button" className="btn-primary" disabled={saveSelection.isPending} onClick={() => saveSelection.mutate()}>{saveSelection.isPending ? 'Creo…' : 'Monitora'}</button>}</div></aside>}
      {saveSelection.isError && <p className="error selection-error">{errorMessage(saveSelection.error)}</p>}
    </div>
  );
}
